import { useState, useEffect } from 'react';
import { PmCard, PmPill } from '../../../components/pm';
import {
  Box, Typography, Button, IconButton, CircularProgress,
  Stack, TextField, Tooltip,
  Dialog, DialogContent, DialogActions,
  Table, TableHead, TableRow, TableCell, TableBody,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import HistoryIcon from '@mui/icons-material/History';
import VisibilityIcon from '@mui/icons-material/Visibility';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useRetroItems, useCreateRetroItem, useUpdateRetroItem, useDeleteRetroItem } from '../../../api/hooks/useRetroItems';
import {
  useRetroSnapshots, useRetroSnapshot, useCreateRetroSnapshot, useDeleteRetroSnapshot,
} from '../../../api/hooks/useRetroSnapshots';
import { useProject } from '../../../api/hooks/useProjects';
import { useSyncMaturityFromCount } from '../../../api/hooks/useReifegrad';
import { useNotifications } from '../../../context/NotificationContext';
import { useAuth } from '../../../context/AuthContext';
import { ConfirmDialog } from '../../../components/common/ConfirmDialog';
import { useAutosave } from '../../../hooks/useAutosave';
import { AutosaveIndicator } from '../../../components/common/AutosaveIndicator';
import { PdfExportButton } from '../../../components/common/PdfExportButton';
import { buildRetroReport } from '../reports/retroReport';
import type { ReportMeta } from '../../../lib/pdf/reportModel';
import { apiErrorMessage } from '../../../lib/apiErrorMessage';
import { AppDialogTitle } from '../../../components/common/AppDialogTitle';

interface RetroItem {
  id: string;
  spalte: 'good' | 'imp' | 'idea' | 'act';
  inhalt: string;
  autor?: string | null;
}

interface RetroSnapshot {
  id: string;
  created_at: string;
}

interface RetroSnapshotDetail extends RetroSnapshot {
  items: RetroItem[];
}

// Interne Spalten-Keys (good/imp/idea/act) bleiben unveraendert — sie sind Teil des
// Persistenz-Modells (Retro-Items UND Snapshots referenzieren sie). Umbenannt wurde
// ausschliesslich die Anzeige: good=Beibehalten(Keep), imp=Fallenlassen(Drop),
// idea=Ausprobieren(Try). act (Maßnahmen) hat keine eigene Spalte mehr (PHQ-93):
// bestehende act-Einträge erscheinen in der Try-Spalte (siehe columnItems).
const COLUMNS = [
  { key: 'good', label: 'Beibehalten (Keep)', color: 'var(--pm-success)', emoji: '😊' },
  { key: 'imp', label: 'Fallenlassen (Drop)', color: 'var(--pm-warning)', emoji: '🧠' },
  { key: 'idea', label: 'Ausprobieren (Try)', color: 'var(--pm-info)', emoji: '💡' },
] as const;

/** Einträge einer Spalte; Try enthält zusätzlich die früheren Maßnahmen (act). */
export function columnItems<T extends { spalte: string }>(all: T[], key: string): T[] {
  return all.filter((it) => it.spalte === key || (key === 'idea' && it.spalte === 'act'));
}

export function RetroTab({ projectId }: { projectId: string }) {
  const { t } = useTranslation('projects');
  const { t: tc } = useTranslation('common');
  const { notify } = useNotifications();
  const { user } = useAuth();

  const { data, isLoading } = useRetroItems(projectId);
  const { data: projectResp } = useProject(projectId);
  const createMut = useCreateRetroItem(projectId);
  const updateMut = useUpdateRetroItem(projectId);
  const deleteMut = useDeleteRetroItem(projectId);
  const items = ((data as unknown as { data?: RetroItem[] } | undefined)?.data ?? []);
  const projectName = (projectResp as unknown as { data?: { name?: string } } | undefined)?.data?.name ?? 'Projekt';
  useSyncMaturityFromCount(projectId, 17, isLoading ? -1 : items.length);

  const [drafts, setDrafts] = useState<Record<string, string>>({ good: '', imp: '', idea: '' });
  const [autor, setAutor] = useState<string>(user?.name ?? user?.email ?? '');
  const [deleteId, setDeleteId] = useState<string | null>(null);

  // Historie/Snapshots (P-21): "Snapshot speichern" legt eine zeitgestempelte Kopie
  // aller aktuellen Retro-Items an; die Historie-Ansicht listet die Snapshots und
  // oeffnet den gespeicherten Stand read-only.
  const createSnapshotMut = useCreateRetroSnapshot(projectId);
  const deleteSnapshotMut = useDeleteRetroSnapshot(projectId);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [viewSnapshotId, setViewSnapshotId] = useState<string | null>(null);
  const [deleteSnapshotId, setDeleteSnapshotId] = useState<string | null>(null);
  const { data: snapshotsResp, isLoading: snapshotsLoading } = useRetroSnapshots(historyOpen ? projectId : undefined);
  const snapshots = ((snapshotsResp as unknown as { data?: RetroSnapshot[] } | undefined)?.data ?? []);
  const { data: snapshotDetailResp, isLoading: snapshotDetailLoading } = useRetroSnapshot(projectId, viewSnapshotId ?? undefined);
  const snapshotDetail = (snapshotDetailResp as unknown as { data?: RetroSnapshotDetail } | undefined)?.data ?? null;

  const addItem = async (spalte: Exclude<RetroItem['spalte'], 'act'>) => {
    const inhalt = drafts[spalte].trim();
    if (!inhalt) return;
    try {
      const payload: Record<string, unknown> = { spalte, inhalt };
      if (autor.trim()) payload.autor = autor.trim();
      await createMut.mutateAsync(payload);
      setDrafts({ ...drafts, [spalte]: '' });
    } catch (err) { notify(apiErrorMessage(err, tc('errors.generic', 'Fehler')), 'error'); }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try { await deleteMut.mutateAsync(deleteId); notify(tc('status.deleted', 'Gelöscht'), 'success'); }
    catch (err) { notify(apiErrorMessage(err, tc('errors.generic', 'Fehler')), 'error'); }
    finally { setDeleteId(null); }
  };

  const handleSnapshot = async () => {
    try {
      await createSnapshotMut.mutateAsync({});
      notify(tc('status.saved', 'Gespeichert'), 'success');
    } catch (err) { notify(apiErrorMessage(err, tc('errors.generic', 'Fehler')), 'error'); }
  };

  const handleDeleteSnapshot = async () => {
    if (!deleteSnapshotId) return;
    try {
      await deleteSnapshotMut.mutateAsync(deleteSnapshotId);
      notify(tc('status.deleted', 'Gelöscht'), 'success');
    } catch (err) { notify(apiErrorMessage(err, tc('errors.generic', 'Fehler')), 'error'); }
    finally { setDeleteSnapshotId(null); }
  };

  if (isLoading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>;

  const pdfMeta: ReportMeta = { projectName, pageTitle: t('retro.title', 'Retrospektive') };
  const buildReport = () => buildRetroReport({ items });

  return (
    <Stack spacing={2}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ sm: 'center' }}>
        <Typography variant="h6">{t('retro.title', 'Retrospektive')}</Typography>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <PdfExportButton meta={pdfMeta} build={buildReport} />
          <Tooltip title={t('retro.snapshotSave', 'Snapshot speichern')}>
            <span>
              <Button
                size="small"
                variant="outlined"
                startIcon={<CameraAltIcon />}
                onClick={() => void handleSnapshot()}
                disabled={createSnapshotMut.isPending || items.length === 0}
              >
                {t('retro.snapshotSave', 'Snapshot speichern')}
              </Button>
            </span>
          </Tooltip>
          <Tooltip title={t('retro.history', 'Historie')}>
            <IconButton size="small" onClick={() => setHistoryOpen(true)} sx={{ color: 'var(--pm-fg-subtle)' }}>
              <HistoryIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <TextField
            size="small"
            label={t('retro.autor', 'Autor')}
            value={autor}
            onChange={(e) => setAutor(e.target.value)}
            placeholder={t('retro.autorPlaceholder', 'Dein Name')}
            sx={{ minWidth: 220 }}
          />
        </Stack>
      </Stack>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' },
          gap: 1.5,
          alignItems: 'flex-start',
        }}
      >
        {COLUMNS.map(({ key, label, color, emoji }) => {
          const colItems = columnItems(items, key);
          return (
            <PmCard key={key} sx={{ borderTop: `4px solid ${color}`, background: 'var(--pm-bg-deep)', minHeight: 280, display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
                <Stack direction="row" alignItems="center" spacing={0.75}>
                  <Box sx={{ fontSize: '1.2rem' }}>{emoji}</Box>
                  <Typography variant="subtitle2" sx={{ color, fontWeight: 600 }}>
                    {t(`retro.columns.${key}`, label)}
                  </Typography>
                </Stack>
                <PmPill tone="neutral">{colItems.length}</PmPill>
              </Stack>
              <Stack spacing={0.75} sx={{ flex: 1 }}>
                {colItems.length === 0 && (
                  <Typography variant="caption" sx={{ color: 'var(--pm-fg-subtle)', textAlign: 'center', py: 2, fontStyle: 'italic' }}>
                    —
                  </Typography>
                )}
                {colItems.map((it) => (
                  <RetroItemCard
                    key={it.id}
                    item={it}
                    onSave={async (inhalt) => { await updateMut.mutateAsync({ id: it.id, data: { inhalt } }); }}
                    onDelete={() => setDeleteId(it.id)}
                  />
                ))}
              </Stack>
              <Stack spacing={0.5} sx={{ mt: 1 }}>
                <TextField
                  size="small"
                  fullWidth
                  placeholder={t('retro.addPlaceholder', 'Eintrag hinzufügen...')}
                  value={drafts[key]}
                  onChange={(e) => setDrafts({ ...drafts, [key]: e.target.value })}
                  onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); void addItem(key); } }}
                  multiline
                  rows={2}
                />
                <Button
                  size="small"
                  fullWidth
                  startIcon={<AddIcon />}
                  onClick={() => void addItem(key)}
                  disabled={!drafts[key].trim()}
                  sx={{ color: 'var(--pm-fg-accent)' }}
                >
                  {tc('actions.add', 'Hinzufügen')}
                </Button>
              </Stack>
            </PmCard>
          );
        })}
      </Box>

      <ConfirmDialog
        open={!!deleteId}
        title={t('retro.deleteTitle', 'Eintrag löschen?')}
        message={t('retro.deleteConfirm', 'Dieser Eintrag wird endgültig entfernt.')}
        onConfirm={handleDelete}
        onCancel={() => setDeleteId(null)}
      />

      <Dialog open={historyOpen} onClose={() => setHistoryOpen(false)} maxWidth="sm" fullWidth>
        <AppDialogTitle>{t('retro.historyTitle', 'Retro-Historie')}</AppDialogTitle>
        <DialogContent>
          {snapshotsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={24} /></Box>
          ) : snapshots.length === 0 ? (
            <Typography variant="body2" sx={{ color: 'var(--pm-fg-subtle)', py: 2 }}>
              {t('retro.historyEmpty', 'Noch keine Snapshots vorhanden.')}
            </Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('retro.historyColCreatedAt', 'Gespeichert am')}</TableCell>
                  <TableCell align="right">{t('retro.historyColActions', 'Aktionen')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {snapshots.map((s) => (
                  <TableRow key={s.id} hover>
                    <TableCell sx={{ fontFamily: 'monospace' }}>
                      {dayjs(s.created_at).format('DD.MM.YYYY HH:mm')}
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title={t('retro.historyView', 'Ansehen')}>
                        <IconButton
                          size="small"
                          onClick={() => { setViewSnapshotId(s.id); setHistoryOpen(false); }}
                          sx={{ color: 'var(--pm-fg-subtle)' }}
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={tc('actions.delete', 'Löschen')}>
                        <IconButton size="small" onClick={() => setDeleteSnapshotId(s.id)} sx={{ color: 'var(--pm-error)' }}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHistoryOpen(false)}>{tc('actions.close', 'Schließen')}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!viewSnapshotId} onClose={() => setViewSnapshotId(null)} maxWidth="lg" fullWidth>
        <AppDialogTitle>
          {snapshotDetail
            ? t('retro.snapshotDetailTitle', 'Snapshot vom {{date}}', { date: dayjs(snapshotDetail.created_at).format('DD.MM.YYYY HH:mm') })
            : t('retro.historyTitle', 'Retro-Historie')}
        </AppDialogTitle>
        <DialogContent>
          {snapshotDetailLoading || !snapshotDetail ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={24} /></Box>
          ) : (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' },
                gap: 1.5,
                alignItems: 'flex-start',
                mt: 1,
              }}
            >
              {COLUMNS.map(({ key, label, color, emoji }) => {
                const colItems = columnItems(snapshotDetail.items, key);
                return (
                  <PmCard key={key} sx={{ borderTop: `4px solid ${color}`, background: 'var(--pm-bg-deep)', minHeight: 160 }}>
                    <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 0.75 }}>
                      <Box sx={{ fontSize: '1.1rem' }}>{emoji}</Box>
                      <Typography variant="subtitle2" sx={{ color, fontWeight: 600 }}>
                        {t(`retro.columns.${key}`, label)}
                      </Typography>
                    </Stack>
                    <Stack spacing={0.75}>
                      {colItems.length === 0 && (
                        <Typography variant="caption" sx={{ color: 'var(--pm-fg-subtle)', fontStyle: 'italic' }}>—</Typography>
                      )}
                      {colItems.map((it) => (
                        <Box
                          key={it.id}
                          sx={{
                            background: 'var(--pm-bg-subtle)', border: '1px solid var(--pm-bg-muted)',
                            borderRadius: 'var(--pm-radius)', padding: 1,
                          }}
                        >
                          <Typography variant="body2" sx={{ fontSize: '0.82rem', color: 'var(--pm-fg-base)' }}>{it.inhalt}</Typography>
                          {it.autor && (
                            <Typography variant="caption" sx={{ color: 'var(--pm-fg-muted)', fontStyle: 'italic' }}>— {it.autor}</Typography>
                          )}
                        </Box>
                      ))}
                    </Stack>
                  </PmCard>
                );
              })}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewSnapshotId(null)}>{tc('actions.close', 'Schließen')}</Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={!!deleteSnapshotId}
        title={t('retro.historyDeleteTitle', 'Snapshot löschen?')}
        message={t('retro.historyDeleteConfirm', 'Dieser Snapshot wird endgültig entfernt.')}
        onConfirm={handleDeleteSnapshot}
        onCancel={() => setDeleteSnapshotId(null)}
      />
    </Stack>
  );
}

/** Retro-Eintrag — Inhalt inline editierbar (Autosave), Autor + Löschen. */
function RetroItemCard({
  item, onSave, onDelete,
}: {
  item: RetroItem;
  onSave: (inhalt: string) => Promise<void>;
  onDelete: () => void;
}) {
  const [value, setValue] = useState(item.inhalt);
  const autosave = useAutosave<string>({
    canSave: (v) => v.trim().length > 0,
    onSave: async (v) => { await onSave(v.trim()); },
  });

  useEffect(() => {
    if (autosave.isDirty) return;
    setValue(item.inhalt);
  }, [autosave.isDirty, item.inhalt]);

  const onChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setValue(e.target.value);
    autosave.schedule(e.target.value);
  };

  return (
    <Box
      sx={{
        background: 'var(--pm-bg-subtle)', border: '1px solid var(--pm-bg-muted)',
        borderRadius: 'var(--pm-radius)', padding: 1, display: 'flex', alignItems: 'flex-start', gap: 1,
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 0.25 }}>
        <TextField
          variant="standard" fullWidth multiline value={value} onChange={onChange}
          onKeyDown={(e) => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) { e.preventDefault(); autosave.flush(); } }}
          slotProps={{ input: { disableUnderline: true, sx: { fontSize: '0.82rem', color: 'var(--pm-fg-base)' } } }}
        />
        {item.autor && (
          <Typography variant="caption" sx={{ color: 'var(--pm-fg-muted)', fontStyle: 'italic' }}>
            — {item.autor}
          </Typography>
        )}
      </Box>
      <Stack alignItems="center" spacing={0.25}>
        <AutosaveIndicator status={autosave.status} onRetry={autosave.flush} compact />
        <IconButton size="small" onClick={onDelete} sx={{ color: 'var(--pm-error)' }}>
          <DeleteIcon fontSize="small" />
        </IconButton>
      </Stack>
    </Box>
  );
}
